import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LANGUAGE_ANSWER_OPTIONS, SITE_LANGUAGE_CODES } from './siteLanguages';
import english from './chartCopy/en.json';

export const CHART_LANGUAGE_KEY = 'nowtarot_birth_chart_language';

const VALID = new Set(
  LANGUAGE_ANSWER_OPTIONS.map((option) => option.value).filter(Boolean),
);

const COPY_LOADERS = {
  es: () => import('./chartCopy/es.json'),
  hi: () => import('./chartCopy/hi.json'),
  zh: () => import('./chartCopy/zh.json'),
  ar: () => import('./chartCopy/ar.json'),
  fr: () => import('./chartCopy/fr.json'),
  pt: () => import('./chartCopy/pt.json'),
  de: () => import('./chartCopy/de.json'),
  ja: () => import('./chartCopy/ja.json'),
  ko: () => import('./chartCopy/ko.json'),
  it: () => import('./chartCopy/it.json'),
  tr: () => import('./chartCopy/tr.json'),
  id: () => import('./chartCopy/id.json'),
  pl: () => import('./chartCopy/pl.json'),
  vi: () => import('./chartCopy/vi.json'),
  th: () => import('./chartCopy/th.json'),
  nl: () => import('./chartCopy/nl.json'),
  ru: () => import('./chartCopy/ru.json'),
  fil: () => import('./chartCopy/fil.json'),
  uk: () => import('./chartCopy/uk.json'),
  ro: () => import('./chartCopy/ro.json'),
  sv: () => import('./chartCopy/sv.json'),
  cs: () => import('./chartCopy/cs.json'),
  el: () => import('./chartCopy/el.json'),
  hu: () => import('./chartCopy/hu.json'),
};

const ChartLanguageContext = createContext(null);

export function languageFromPath(pathname) {
  const code = String(pathname || '').split('/')[1];
  return SITE_LANGUAGE_CODES.includes(code) ? code : 'en';
}

export function chartHref(path, lang, extra) {
  const [base, query = ''] = String(path || '').split('?');
  const params = new URLSearchParams(query);
  if (lang && lang !== 'en' && VALID.has(lang)) params.set('lang', lang);
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (value != null && value !== '') params.set(key, String(value));
    });
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function fill(template, values = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (match, key) => (
    values[key] == null ? match : String(values[key])
  ));
}

function readStored() {
  try {
    const value = localStorage.getItem(CHART_LANGUAGE_KEY);
    if (value && VALID.has(value)) return value;
  } catch {
    // private mode
  }
  return '';
}

function writeStored(value) {
  try {
    localStorage.setItem(CHART_LANGUAGE_KEY, value);
  } catch {
    // private mode
  }
}

function useChartLanguageState() {
  const { search } = useLocation();
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    const query = new URLSearchParams(search).get('lang');
    if (query && VALID.has(query)) {
      setLanguage(query);
      writeStored(query);
      return;
    }
    const stored = readStored();
    if (stored) setLanguage(stored);
  }, [search]);

  const chooseLanguage = useCallback((value) => {
    if (!VALID.has(value)) return;
    setLanguage(value);
    writeStored(value);
  }, []);

  return { language, chooseLanguage };
}

export function ChartLanguageProvider({ children }) {
  const value = useChartLanguageState();
  return (
    <ChartLanguageContext.Provider value={value}>
      {children}
    </ChartLanguageContext.Provider>
  );
}

export function useChartLanguage() {
  const context = useContext(ChartLanguageContext);
  const local = useChartLanguageState();
  return context || local;
}

export function useChartCopy(lang) {
  const [copy, setCopy] = useState(english);

  useEffect(() => {
    if (!lang || lang === 'en') {
      setCopy(english);
      return undefined;
    }
    const load = COPY_LOADERS[lang];
    if (!load) {
      setCopy(english);
      return undefined;
    }
    let live = true;
    load()
      .then((mod) => {
        if (live) setCopy(mod.default || mod);
      })
      .catch(() => {
        if (live) setCopy(english);
      });
    return () => {
      live = false;
    };
  }, [lang]);

  return copy;
}

export function EmText({ text }) {
  const parts = String(text || '').split('*');
  return parts.map((part, index) => (
    index % 2 === 1 ? <em key={index}>{part}</em> : <span key={index}>{part}</span>
  ));
}

export function LinkedNote({ template, links }) {
  const parts = String(template || '').split(/(\{zodiac\}|\{chart\})/g);
  return parts.map((part, index) => {
    const link = part === '{zodiac}' ? links.zodiac : part === '{chart}' ? links.chart : null;
    if (!link) return <span key={index}>{part}</span>;
    return <Link key={index} to={link.href}>{link.label}</Link>;
  });
}

export function chartDirection(language) {
  if (!language || language === 'en') return {};
  return {
    lang: language,
    dir: language === 'ar' ? 'rtl' : undefined,
  };
}
